import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/common/States";
import { useSession } from "@/context/SessionContext";
import { errMsg } from "@/lib/format";

const EMPTY = { name: "", email: "", role: "", branch_ids: [], all_branches: false, approval_limit: "", phone: "", designation: "", status: "active", extra_permissions: [], revoked_permissions: [], delegate_user_id: "", delegate_until: "" };
const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const AccessTab = ({ f, set, setF, branches, roles, labels, isEdit }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <Field label="Full name *"><Input required value={f.name} onChange={set("name")} data-testid="user-name-input" /></Field>
    <Field label="Email *" hint={isEdit ? "Email cannot be changed" : "They sign in with this email"}><Input required type="email" disabled={isEdit} value={f.email} onChange={set("email")} data-testid="user-email-input" /></Field>
    <Field label="Role *">
      <Select value={f.role} onValueChange={set("role")}>
        <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
        <SelectContent>{roles.map((r) => <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>{labels[r] || r}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
    <Field label="Approval limit (₹)" hint="Leave empty for no approval authority"><Input type="number" min="0" value={f.approval_limit} onChange={set("approval_limit")} data-testid="user-approval-limit-input" /></Field>
    <Field label="Designation"><Input value={f.designation} onChange={set("designation")} data-testid="user-designation-input" /></Field>
    <Field label="Phone"><Input value={f.phone} onChange={set("phone")} data-testid="user-phone-input" /></Field>
    {isEdit && (
      <Field label="Status">
        <Select value={f.status} onValueChange={set("status")} disabled={!["active", "inactive"].includes(f.status)}>
          <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem>
            {!["active", "inactive"].includes(f.status) && <SelectItem value={f.status}>{f.status}</SelectItem>}
          </SelectContent>
        </Select>
      </Field>
    )}
    <div className="sm:col-span-2 space-y-3">
      <div className="flex items-center gap-3"><Switch checked={f.all_branches} onCheckedChange={set("all_branches")} data-testid="user-all-branches-switch" /><span className="text-sm">Access to all branches</span></div>
      {!f.all_branches && (
        <div className="grid sm:grid-cols-2 gap-2 border rounded-md p-3" data-testid="user-branch-picker">
          {branches.map((b) => (
            <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={f.branch_ids.includes(b.id)} onCheckedChange={() => setF((p) => ({ ...p, branch_ids: toggle(p.branch_ids, b.id) }))} data-testid={`user-branch-${b.code}`} />
              {b.name} <span className="text-xs text-muted-foreground font-mono-g">{b.code}</span>
            </label>
          ))}
          {!branches.length && <span className="text-sm text-muted-foreground">No branches available</span>}
        </div>
      )}
    </div>
  </div>
);

const OverridesTab = ({ f, setF, catalog }) => (
  <div className="max-h-[50vh] overflow-y-auto border rounded-md" data-testid="user-permission-overrides">
    <div className="grid grid-cols-[1fr_70px_70px] text-[11px] g39-label px-3 py-2 border-b sticky top-0 bg-white"><span>Permission</span><span className="text-center">Grant</span><span className="text-center">Revoke</span></div>
    {catalog.modules.map((m) => m.permissions.map((p) => (
      <div key={p} className="grid grid-cols-[1fr_70px_70px] items-center px-3 py-1.5 text-sm border-b last:border-0">
        <span><span className="text-muted-foreground">{m.label} ·</span> {p.split(".")[1]}</span>
        <span className="flex justify-center"><Checkbox checked={f.extra_permissions.includes(p)} onCheckedChange={() => setF((x) => ({ ...x, extra_permissions: toggle(x.extra_permissions, p), revoked_permissions: x.revoked_permissions.filter((y) => y !== p) }))} data-testid={`grant-${p}`} /></span>
        <span className="flex justify-center"><Checkbox checked={f.revoked_permissions.includes(p)} onCheckedChange={() => setF((x) => ({ ...x, revoked_permissions: toggle(x.revoked_permissions, p), extra_permissions: x.extra_permissions.filter((y) => y !== p) }))} data-testid={`revoke-${p}`} /></span>
      </div>
    )))}
  </div>
);

const DelegationTab = ({ f, set, users, selfId }) => (
  <div className="grid sm:grid-cols-2 gap-4" data-testid="user-delegation">
    <Field label="Delegate approvals to">
      <Select value={f.delegate_user_id || "none"} onValueChange={(v) => set("delegate_user_id")(v === "none" ? "" : v)}>
        <SelectTrigger data-testid="user-delegate-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No delegation</SelectItem>
          {users.filter((u) => u.id !== selfId && u.status === "active").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
    <Field label="Delegation valid until"><Input type="date" value={f.delegate_until || ""} onChange={set("delegate_until")} data-testid="user-delegate-until-input" /></Field>
    <p className="sm:col-span-2 text-xs text-muted-foreground">Delegation is recorded now and will be applied by the Approval Center in Phase 3.</p>
  </div>
);

export const UserFormDialog = ({ open, onOpenChange, user, catalog, users, onSaved }) => {
  const { api, me, branches } = useSession();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!user;
  const labels = Object.fromEntries((catalog?.roles || []).map((r) => [r.key, r.label]));
  const roles = me?.assignable_roles || [];
  const editRoles = isEdit && user.role && !roles.includes(user.role) ? [user.role, ...roles] : roles;

  useEffect(() => {
    if (open) setF(user ? { ...EMPTY, ...Object.fromEntries(Object.entries(user).map(([k, v]) => [k, v ?? EMPTY[k] ?? ""])) } : EMPTY);
  }, [open, user]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.role) return toast.error("Select a role");
    setSaving(true);
    const limit = f.approval_limit === "" || f.approval_limit === null ? null : Number(f.approval_limit);
    try {
      let data;
      if (isEdit) {
        const body = { name: f.name, role: f.role, branch_ids: f.branch_ids, all_branches: f.all_branches, phone: f.phone, designation: f.designation, extra_permissions: f.extra_permissions, revoked_permissions: f.revoked_permissions };
        if (limit === null) body.clear_approval_limit = true; else body.approval_limit = limit;
        if (["active", "inactive"].includes(f.status)) body.status = f.status;
        if (f.delegate_user_id) { body.delegate_user_id = f.delegate_user_id; body.delegate_until = f.delegate_until || null; } else body.clear_delegate = true;
        if (user.id === me.user.id) ["role", "branch_ids", "all_branches", "status", "extra_permissions", "revoked_permissions", "clear_approval_limit", "approval_limit"].forEach((k) => delete body[k]);
        ({ data } = await api.patch(`/users/${user.id}`, body, { noBranch: true }));
        toast.success("User updated");
      } else {
        ({ data } = await api.post("/users", { name: f.name, email: f.email, role: f.role, branch_ids: f.branch_ids, all_branches: f.all_branches, approval_limit: limit, phone: f.phone, designation: f.designation }, { noBranch: true }));
        toast.success(data.invite_status === "sent" ? "User created and invitation email sent" : "User created — they can sign up with this email");
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit user" : "Add user"}</DialogTitle>
          <DialogDescription>Access is limited to the selected company{isEdit ? "" : " — the user signs in with this email"}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit}>
          <Tabs defaultValue="access">
            <TabsList className="mb-4">
              <TabsTrigger value="access" data-testid="user-tab-access">Profile & access</TabsTrigger>
              {isEdit && <TabsTrigger value="overrides" data-testid="user-tab-overrides">Permission overrides</TabsTrigger>}
              {isEdit && <TabsTrigger value="delegation" data-testid="user-tab-delegation">Delegation</TabsTrigger>}
            </TabsList>
            <TabsContent value="access"><AccessTab f={f} set={set} setF={setF} branches={branches} roles={editRoles} labels={labels} isEdit={isEdit} /></TabsContent>
            {isEdit && catalog && <TabsContent value="overrides"><OverridesTab f={f} setF={setF} catalog={catalog} /></TabsContent>}
            {isEdit && <TabsContent value="delegation"><DelegationTab f={f} set={set} users={users || []} selfId={user.id} /></TabsContent>}
          </Tabs>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="user-save-button">{saving ? "Saving…" : isEdit ? "Save changes" : "Create user"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
